import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    IonTitle,
    IonToast,
    IonToolbar,
    RefresherEventDetail,
    useIonRouter
} from "@ionic/react";
import {
    arrowBackOutline,
    checkmarkCircleOutline,
    personOutline,
    refreshOutline,
    saveOutline,
    shirtOutline
} from "ionicons/icons";

import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    useHistory,
    useParams
} from "react-router-dom";
import Cookies from "js-cookie";
import "./TeamSettings.css";

interface TeamSettingsData {
    leagueName: string;
    teamName: string;
    manager: string;
    role: string;
}

function normalizeText(value: unknown): string {
    return String(value || "").trim();
}

function getLeagueName(data: unknown): string {
    if (!data || typeof data !== "object") {
        return "Liga Fantasy";
    }

    const league = data as {
        Info?: {
            name?: string;
        };
    };

    return (
        normalizeText(league.Info?.name) ||
        "Liga Fantasy"
    );
}

function getTeamData(
    data: unknown,
    userId: string
): {
    teamName: string;
    manager: string;
    role: string;
} {
    if (!data || typeof data !== "object" || !userId) {
        return {
            teamName: "Mi equipo",
            manager: "",
            role: ""
        };
    }

    const league = data as {
        Teams?: Record<
            string,
            {
                teamName1?: string;
                manager?: string;
                user?: string;
            }
        >;
    };

    const team = league.Teams?.[userId];

    return {
        teamName:
            normalizeText(team?.teamName1) ||
            "Mi equipo",

        manager: normalizeText(team?.manager),

        role: normalizeText(team?.user)
    };
}

function getRoleLabel(role: string): string {
    const normalizedRole = role.toLowerCase();

    if (
        normalizedRole === "comisioner" ||
        normalizedRole === "commissioner"
    ) {
        return "Comisionado";
    }

    return "Manager";
}

function validateTeamName(value: string): string {
    const teamName = value.trim();

    if (!teamName) {
        return "Escribe un nombre para tu equipo.";
    }

    if (teamName.length < 3) {
        return "El nombre debe tener al menos 3 caracteres.";
    }

    if (teamName.length > 20) {
        return "El nombre no puede superar 20 caracteres.";
    }

    /*
        Permite letras, números, espacios, acentos y guiones.
    */
    if (!/^[\p{L}\p{N} ]+(?:[- ][\p{L}\p{N}]+)*$/u.test(teamName)) {
        return (
            "Usa únicamente letras, números, espacios y guiones."
        );
    }

    return "";
}

const TeamSettings: React.FC = () => {
    const router = useIonRouter();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();

function goToSettingsMenu(): void {
    history.replace(`/fantasySettingsMenu/${id}`);
}


    const [data, setData] =
        useState<TeamSettingsData | null>(null);

    const [teamName, setTeamName] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    function getAccessToken(): string {
        return Cookies.get("access_token") || "";
    }

    function getAccessUsername(): string {
    return (
        Cookies.get("access_username") ||
        Cookies.get("access_user") ||
        ""
    );
}

    const loadTeamSettings = useCallback(
        async function (forceRefresh = false) {
            const accessToken = getAccessToken();

            if (!accessToken) {
                router.push("/home", "root", "replace");
                return;
            }

            try {
                setError("");

                if (!forceRefresh) {
                    setLoading(true);
                }

                const dataRoot = process.env.PUBLIC_URL || "";

                const response = await fetch(
                    `${dataRoot}/fantasy_data/${id}.json?t=${Date.now()}`,
                    {
                        cache: "no-store"
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `No fue posible cargar tu equipo. Código: ${response.status}`
                    );
                }

                const leagueData = await response.json();

                const league = leagueData as {
    Teams?: Record<string, unknown>;
};

if (!league.Teams?.[accessToken]) {
    throw new Error(
        "No perteneces a esta liga o tu equipo ya no está disponible."
    );
}


                const teamData = getTeamData(
                    leagueData,
                    accessToken
                );

                setData({
                    leagueName: getLeagueName(leagueData),
                    teamName: teamData.teamName,
                    manager: teamData.manager,
                    role: teamData.role
                });

                setTeamName(teamData.teamName);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar TeamSettings:",
                    requestError
                );

                setError(
                    "No fue posible cargar la configuración de tu equipo."
                );
            } finally {
                setLoading(false);
            }
        },
        [id, router]
    );

    useEffect(
        function () {
            loadTeamSettings();
        },
        [loadTeamSettings]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadTeamSettings(true);
        event.detail.complete();
    }

    async function saveTeamSettings() {
        const normalizedTeamName = teamName.trim();

        const validationError = validateTeamName(
            normalizedTeamName
        );

        if (validationError) {
            setMessage(validationError);
            return;
        }

        if (saving) {
            return;
        }

        try {
            setSaving(true);

            const response = await fetch(
                "https://fantasy.ftblscore.com/create_league/" +
                    "control_TeamSettings.php",
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        "X-Access-Token": getAccessToken(),
                        "X-Access-Username": getAccessUsername()
                    },
                    body: JSON.stringify({
                        leagueId: id,
                        teamName1: normalizedTeamName
                    })
                }
            );

            const rawResponse = await response.text();

            let result: {
                success?: boolean;
                message?: string;
                team?: {
                    teamName1?: string;
                };
            };

            try {
                result = JSON.parse(rawResponse);
            } catch {
                console.error(
                    "control_TeamSettings.php no devolvió JSON:",
                    rawResponse
                );

                throw new Error(
                    "El servidor no devolvió una respuesta válida."
                );
            }

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                        "No fue posible guardar el nombre del equipo."
                );
            }

            const savedName =
                result.team?.teamName1 ||
                normalizedTeamName;

            setTeamName(savedName);

            setData(function (currentData) {
                if (!currentData) {
                    return currentData;
                }

                return {
                    ...currentData,
                    teamName: savedName
                };
            });

            setMessage(
                result.message ||
                    "Nombre del equipo actualizado correctamente."
            );
        } catch (requestError) {
            console.error(
                "No fue posible guardar TeamSettings:",
                requestError
            );

            setMessage(
                requestError instanceof Error
                    ? requestError.message
                    : "No fue posible guardar los cambios."
            );
        } finally {
            setSaving(false);
        }
    }

    const nameError = teamName
        ? validateTeamName(teamName)
        : "";

    const hasChanges =
    data !== null &&
    teamName.trim() !== data.teamName;


    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="team-settings-toolbar">
                <IonButtons slot="start">
    <IonButton
        fill="clear"
        color="medium"
        aria-label="Regresar a configuración"
        onClick={goToSettingsMenu}
    >
        <IonIcon icon={arrowBackOutline} />
    </IonButton>
</IonButtons>

                    <IonTitle>Mi equipo</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Guardar cambios"
                            disabled={
                                !data ||
                                !hasChanges ||
                                Boolean(nameError) ||
                                saving
                            }
                            onClick={saveTeamSettings}
                        >
                            {saving ? (
                                <IonSpinner name="crescent" />
                            ) : (
                                <IonIcon
                                    slot="icon-only"
                                    icon={saveOutline}
                                />
                            )}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="team-settings-content"
            >
                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando equipo..."
                    />
                </IonRefresher>

                {loading && (
                    <IonProgressBar
                        className="team-settings-progress"
                        type="indeterminate"
                    />
                )}

                {!loading && error && (
                    <main className="team-settings-main">
                        <div className="team-settings-empty">
                            <IonIcon icon={refreshOutline} />
                            <strong>
                                No pudimos cargar tu equipo
                            </strong>
                            <span>{error}</span>

                            <IonButton
                                fill="outline"
                                size="small"
                                onClick={function () {
                                    loadTeamSettings(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    </main>
                )}

                {!loading && !error && data && (
                    <main className="team-settings-main">
                        <section className="team-settings-hero">
                            <div className="team-settings-hero__icon">
                                <IonIcon icon={shirtOutline} />
                            </div>

                            <div>
                                <span>CONFIGURACIÓN DE EQUIPO</span>
                                <h1>{data.teamName}</h1>
                                <p>{data.leagueName}</p>
                            </div>
                        </section>

                        <section className="team-settings-summary">
                            <div>
                                <IonIcon icon={personOutline} />

                                <div>
                                    <span>MANAGER</span>
                                    <strong>
                                        {data.manager || "Jugador"}
                                    </strong>
                                </div>
                            </div>

                            <div>
                                <IonIcon
                                    icon={checkmarkCircleOutline}
                                />

                                <div>
                                    <span>ROL</span>
                                    <strong>
                                        {getRoleLabel(data.role)}
                                    </strong>
                                </div>
                            </div>
                        </section>

                        <section className="team-settings-card">
                            <header className="team-settings-card__header">
                                <span>IDENTIDAD DEL EQUIPO</span>
                                <h2>Nombre del equipo</h2>
                                <p>
                                    Este nombre será visible para los
                                    demás participantes de la liga.
                                </p>
                            </header>

                            <IonItem
                                className="team-settings-input"
                                lines="none"
                            >
                                <IonLabel position="stacked">
                                    Nombre visible
                                </IonLabel>

                                <IonInput
                                    value={teamName}
                                    maxlength={20}
                                    disabled={saving}
                                    placeholder="Ejemplo: Borregos"
                                    onIonChange={function (event) {
                                        setTeamName(
                                            event.detail.value || ""
                                        );
                                    }}
                                />

                            </IonItem>
<div className="team-settings-character-count">
    {teamName.length}/20 caracteres
</div>
                            {nameError && (
                                <p className="team-settings-validation">
                                    {nameError}
                                </p>
                            )}

                            <p className="team-settings-help">
                                Máximo 20 caracteres. Puedes usar
                                letras, números, espacios y guiones.
                            </p>

                            <IonButton
                                className="team-settings-save"
                                expand="block"
                                disabled={
                                    !hasChanges ||
                                    Boolean(nameError) ||
                                    saving
                                }
                                onClick={saveTeamSettings}
                            >
                                {saving ? (
                                    <>
                                        <IonSpinner name="crescent" />
                                        Guardando cambios...
                                    </>
                                ) : (
                                    <>
                                        Guardar nombre del equipo
                                        <IonIcon
                                            slot="end"
                                            icon={saveOutline}
                                        />
                                    </>
                                )}
                            </IonButton>
                        </section>
                    </main>
                )}
            </IonContent>

            <IonToast
                isOpen={Boolean(message)}
                message={message}
                duration={3200}
                position="bottom"
                onDidDismiss={function () {
                    setMessage("");
                }}
            />
        </IonPage>
    );
};

export default TeamSettings;
